import styled from "styled-components";
import { Flex, Text, Image } from "rebass/styled-components";
import { FaPlay } from "react-icons/fa";
import { useRecoilValue } from "recoil";
import { useStatsigClient } from "@statsig/react-bindings";

import "./index.css";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import {
  RenderPreMatchState,
  DisplayEmptyState,
} from "MVVM/State/embeddablePlaylist";
import { formatNumber } from "MVVM/Utilities/Clip";
import {
  TopViewedPlayers,
  TopViewedMatches,
  TopViewedTeam,
  TopViewedPlayersList,
  TopViewedMatchesList,
  TopViewedTeamsList,
  TopViewedPlayersByCountryList,
  TopViewedPlayersByCountry,
} from "MVVM/Views/EmbeddablePlaylist/@types";

export const TopViewedListContainer = ({
  topViewedPlayers,
  topViewedPlayersByCountry,
  topViewedMatches,
  topViewedTeams,
  theme = "night",
}: {
  topViewedPlayers: TopViewedPlayersList | null;
  topViewedPlayersByCountry: TopViewedPlayersByCountryList | null;
  topViewedMatches: TopViewedMatchesList | null;
  topViewedTeams: TopViewedTeamsList | null;
  theme?: string;
}) => {
  const renderPreMatchState = useRecoilValue(RenderPreMatchState);
  const displayEmptyState = useRecoilValue(DisplayEmptyState);

  return (
    <StyledContainer
      $renderPreMatchState={renderPreMatchState}
      $displayEmptyState={displayEmptyState}
    >
      <StyledColumn>
        <StyledHeader>Trending Players</StyledHeader>
        {!!topViewedPlayers &&
          topViewedPlayers.map((player: TopViewedPlayers) => (
            <PlayerRow player={player} />
          ))}
      </StyledColumn>
      <StyledColumn className="rising-stars-column">
        {!!topViewedPlayersByCountry && topViewedPlayersByCountry.length > 0 ? (
          <>
            <StyledHeader>Rising Stars</StyledHeader>
            {topViewedPlayersByCountry.map(
              (player: TopViewedPlayersByCountry) => (
                <PlayerRow player={player} />
              ),
            )}
          </>
        ) : !!topViewedTeams && topViewedTeams.length > 0 ? (
          <>
            <StyledHeader>Trending Teams</StyledHeader>
            {!!topViewedTeams &&
              topViewedTeams.map((team: TopViewedTeam) => (
                <TeamRow team={team} />
              ))}
          </>
        ) : null}
      </StyledColumn>
      <StyledColumn className="matches-column">
        <StyledHeader>Most Viewed Matches</StyledHeader>
        {!!topViewedMatches &&
          topViewedMatches.map((match: TopViewedMatches) => (
            <MatchRow match={match} />
          ))}
      </StyledColumn>
    </StyledContainer>
  );
};

const StyledColumn = styled(Flex)`
  flex-direction: column;
  flex: 1;
  gap: 5px;
  height: 100%;
  overflow: hidden;
`;

const StyledContainer = styled(Flex)<{
  $renderPreMatchState: boolean;
  $displayEmptyState: boolean;
}>`
  container-name: top-viewed-list-container;
  container-type: size;

  background-color: ${({ $displayEmptyState }) =>
    $displayEmptyState ? "#1b2024" : "inherit"};
  height: ${({ $renderPreMatchState }) =>
    $renderPreMatchState ? "184px" : "155px"};
  gap: 10px;
  overflow: ${({ $renderPreMatchState }) =>
    $renderPreMatchState ? "hidden" : "visible"};
  padding: ${({ $displayEmptyState }) =>
    $displayEmptyState ? "0px 10px" : "0px 5px 0px"};
  width: 100%;

  ${({ $renderPreMatchState }) =>
    $renderPreMatchState
      ? `
      @media (max-width: 502px) {
        height: 150px;
      }

      @media (max-width: 380px) {
        height: 120px;
      }
    `
      : `
      @media (max-width: 400px) {
        height: 120px;
      }
    `}

  @media (max-width: 502px) {
    .rising-stars-column {
      display: none;
    }
  }
`;

const StyledHeader = styled(Text)`
  color: ${({ theme }) => theme.colors.shade100};
  font-size: 14px;
  font-weight: 700;
  padding: 6px 0 1px;
`;

export const PlayerRow = ({
  player,
}: {
  player: TopViewedPlayers | TopViewedPlayersByCountry;
}) => {
  const { track } = useAnalyticsContext();
  const { getExperiment } = useStatsigClient();
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const platform = new URLSearchParams(window.location.search).get("platform");
  const showProfileCTA = getExperiment("hltv_pro_player_profile_ctas").get(
    "show_profile_cta",
    false,
  );

  const playerGameId = player.metadata.playerGameIdentifier;

  return (
    <StyledRow
      onClick={() => {
        track("Button - Click", {
          location: "trending_content_list",
          locationIdentifier: "top_player",
          source: "website",
          platform: "HLTV.ORG",
          clipId: player.clip,
          playlistId: player.playlist,
        });

        if (showProfileCTA && playerGameId) {
          const domain = window.location.origin;
          const profileUrl = `${domain}/u/${playerGameId}?utm_source=${platform}&ajs_aid=${ajs_aid}`;
          return window.open(profileUrl, "_blank");
        }

        window.open(
          `${window.location.origin}/clip/${player.clip}?context=playlist&playlistId=${player.playlist}&tab=recommended&ajs_aid=${ajs_aid}&utm_source=${platform}`,
          "_blank",
        );
      }}
    >
      <Flex style={{ flex: "0 0 auto", alignItems: "center", gap: "5px" }}>
        <StyledCountryFlag
          src={`${
            process.env.REACT_APP_S3_CLIP_BUCKET
          }/static/web/flags/${player.metadata.playerCtryISO.toLowerCase()}.png`}
        />
        <StyledTeamLogo src={player.metadata.teamLogo} />
      </Flex>
      <Flex style={{ flex: "1" }}>
        <StyledText className="styled-text">
          {player.metadata.playerName}
        </StyledText>
      </Flex>
      <StyledViewsContainer>
        <PlayIcon />
        <StyledText className="styled-text">
          {formatNumber(player.views)}
        </StyledText>
      </StyledViewsContainer>
    </StyledRow>
  );
};

export const MatchRow = ({ match }: { match: TopViewedMatches }) => {
  const { track } = useAnalyticsContext();
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const platform = new URLSearchParams(window.location.search).get("platform");
  const team1 = match.metadata[0];
  const team2 = match.metadata[1];

  return (
    <StyledRow
      onClick={() => {
        track("Button - Click", {
          location: "trending_content_list",
          locationIdentifier: "top_match",
          source: "website",
          platform: "HLTV.ORG",
          clipId: match.clip,
          playlistId: match.playlist,
        });

        window.open(
          `${window.location.origin}/clip/${match.clip}?context=playlist&playlistId=${match.playlist}&tab=recommended&ajs_aid=${ajs_aid}&utm_source=${platform}`,
          "_blank",
        );
      }}
    >
      <Flex style={{ flex: 1, gap: "5px" }}>
        <StyledTeamLogo src={team1.teamLogo} />
        <StyledText className="styled-text">{team1.teamName}</StyledText>
        <StyledText style={{ fontWeight: 400 }} className="styled-text">
          vs
        </StyledText>
        <StyledTeamLogo src={team2.teamLogo} />
        <StyledText className="styled-text">{team2.teamName}</StyledText>
      </Flex>
      <StyledViewsContainer>
        <PlayIcon />
        <StyledText className="styled-text">
          {formatNumber(match.views)}
        </StyledText>
      </StyledViewsContainer>
    </StyledRow>
  );
};

export const TeamRow = ({ team }: { team: TopViewedTeam }) => {
  const { track } = useAnalyticsContext();
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const platform = new URLSearchParams(window.location.search).get("platform");
  return (
    <StyledRow
      style={{ gap: "10px", alignItems: "center" }}
      onClick={() => {
        track("Button - Click", {
          location: "trending_content_list",
          locationIdentifier: "rising_star",
          source: "website",
          platform: "HLTV.ORG",
          clipId: team.clip,
          playlistId: team.playlist,
        });

        window.open(
          `${window.location.origin}/clip/${team.clip}?context=playlist&playlistId=${team.playlist}&tab=recommended&ajs_aid=${ajs_aid}&utm_source=${platform}`,
          "_blank",
        );
      }}
    >
      <StyledTeamLogo
        src={team.metadata.teamLogo}
        style={{ flex: "0 0 auto" }}
      />
      <StyledText style={{ flex: "1" }} className="styled-text">
        {team.metadata.teamName}
      </StyledText>
      <StyledCountryFlag
        src={`${
          process.env.REACT_APP_S3_CLIP_BUCKET
        }/static/web/flags/${team.metadata.teamCtryISO.toLowerCase()}.png`}
        style={{ flex: "0 0 auto" }}
      />
    </StyledRow>
  );
};

const StyledRow = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.midnight};
  border: ${({ theme }) => `1px solid ${theme.colors.navy50}`};
  border-radius: 6px;
  cursor: pointer;
  gap: 5px;
  padding: 5px;

  :hover {
    background: linear-gradient(0deg, #131d25, #131d25),
      linear-gradient(0deg, rgba(77, 189, 148, 0.1), rgba(77, 189, 148, 0.1));
    border: ${({ theme }) => `1px solid ${theme.colors.envy}`};
  }

  :hover .styled-text {
    color: ${({ theme }) => theme.colors.shade100};
  }
`;

const StyledViewsContainer = styled(Flex)`
  align-items: center;
  flex: 0 0 auto;
  gap: 5px;
  margin-left: auto;
`;

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colors.shade300};
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledCountryFlag = styled(Image)`
  height: 8px;
  width: 12px;
`;

const StyledTeamLogo = styled(Image)`
  height: 12px;
  width: 12px;
`;

const PlayIcon = styled(FaPlay)`
  height: 6px;
  color: ${({ theme }) => theme.colors.envy};
  width: 6px;
`;
